/**
 * Booking Financial View
 * Editable: Discount codes
 * Read-only: All financial calculations, payment info
 * Shows paid amount vs outstanding with payment breakdown by channel
 * Supports recording manual payments and sending Stripe payment links
 * Includes manual refund functionality with Stripe integration
 */

import { useState, useEffect } from 'react';
import {
  Receipt, Tag, CreditCard, Loader2, AlertCircle, X,
  RotateCcw, Clock, BookOpen, DollarSign, Send, ExternalLink,
  Plus, Banknote, Wallet, Shield, AlertTriangle, Wrench, Mail
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { validateDiscountCode } from '../services/discountCodes.service';
import { getRefundHistory, processRefund, type RefundRecord } from '../services/refunds.service';
import {
  getPaymentTransactions,
  recordManualPayment,
  createPaymentLink,
  chargeOffSession,
  getChannelLabel,
  getStripeTransactionTotal,
  getNonStripeTransactionTotal,
  PAYMENT_CHANNELS,
  type PaymentTransaction,
  type TransactionType,
} from '../services/paymentTransactions.service';
import { performDepositAction, type DepositAction } from '../services/deposits.service';
import { sendPaymentEmail } from '../services/manageLink.service';
import { getMerchantById } from '../services/merchants.service';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatCurrency } from '../lib/currency';
import { deriveRecordedBookingPaid } from '../services/analytics.service';
import { computeBookingTotals } from '../lib/bookingTotals';
import { useDateFormatter } from '../hooks/useDateFormatter';
import type { Database } from '../lib/database.types';

type Booking = Database['public']['Tables']['bookings']['Row'];

interface BookingFinancialViewProps {
  booking: Booking;
  originalBooking: Booking | null;
  onChange: (updates: Partial<Booking>) => void;
  disabled: boolean;
  onRefundProcessed?: () => void;
  onPaymentRecorded?: () => void;
}

type RefundMode = 'percentage' | 'fixed';

export default function BookingFinancialView({
  booking,
  onChange,
  disabled,
  onRefundProcessed,
  onPaymentRecorded,
}: BookingFinancialViewProps) {
  const { t } = useTranslation();
  const { merchantId, user, hasRole, isPlatformAdmin, roles } = useAuth();
  const { showToast } = useToast();
  const { formatDateTimeCompact } = useDateFormatter();

  // Discount state
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [validating, setValidating] = useState(false);
  const [discountError, setDiscountError] = useState<string | null>(null);

  // Payment link state
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);
  const [paymentLinkUrl, setPaymentLinkUrl] = useState<string | null>(null);
  const [sendingPaymentEmail, setSendingPaymentEmail] = useState(false);

  // Payment transactions state
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  // Manual payment form state
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [manualPaymentAmount, setManualPaymentAmount] = useState('');
  const [manualPaymentChannel, setManualPaymentChannel] = useState('cash');
  const [manualPaymentNotes, setManualPaymentNotes] = useState('');
  const [recordingPayment, setRecordingPayment] = useState(false);

  // Refund state
  const [refundHistory, setRefundHistory] = useState<RefundRecord[]>([]);
  const [loadingRefunds, setLoadingRefunds] = useState(false);
  const [refundMode, setRefundMode] = useState<RefundMode>('percentage');
  const [refundInput, setRefundInput] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [processingRefund, setProcessingRefund] = useState(false);
  // Whether the refund being prepared returns money for items removed from the
  // booking / an overpayment (refund_history.restores_balance). null = the
  // operator has not touched the checkbox, so it follows the default below.
  const [refundRestoresBalanceChoice, setRefundRestoresBalanceChoice] = useState<boolean | null>(null);
  // External refund (bookkeeping-only) — checked when the refund has already
  // been issued outside the admin panel (PayPal dashboard, Stripe dashboard,
  // bank transfer, cash) and we just want to record it in the ledger.
  const [isExternalRefund, setIsExternalRefund] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Deposit state
  const [depositAction, setDepositAction] = useState<DepositAction | null>(null);
  const [depositAmountInput, setDepositAmountInput] = useState('');
  const [processingDeposit, setProcessingDeposit] = useState(false);

  // Off-session damage charge state
  const [merchantOffSessionEnabled, setMerchantOffSessionEnabled] = useState<boolean | null>(null);
  const [showOffSessionForm, setShowOffSessionForm] = useState(false);
  const [offSessionAmount, setOffSessionAmount] = useState('');
  const [offSessionReason, setOffSessionReason] = useState('');
  const [chargingOffSession, setChargingOffSession] = useState(false);
  const [offSessionRequiresAction, setOffSessionRequiresAction] = useState<{
    paymentIntentId: string | null;
    message: string;
  } | null>(null);

  // Refund scope selector — only relevant when a booking has both booking_payment
  // and damage_charge transactions. Defaults to null = legacy unscoped behaviour.
  const [refundScope, setRefundScope] = useState<TransactionType | null>(null);

  // Permission check: viewer cannot refund or record payments
  const canManagePayments = hasRole('operator');

  // Off-session-charge authority: platform admin OR merchant admin on this
  // booking's merchant. Used by the gate AND the merchant-config fetch effect.
  const isMerchantAdminOnBooking =
    isPlatformAdmin ||
    roles.some(r => r.merchant_id === booking.merchant_id && r.role === 'admin');

  // Load refund history and payment transactions on mount
  useEffect(() => {
    if (booking.id) {
      loadRefundHistory();
      loadTransactions();
    }
  }, [booking.id]);

  // Load merchant config for off-session-charge eligibility gating. Only fetch
  // when the user has potential authority — saves a request for everyone else.
  useEffect(() => {
    if (!isMerchantAdminOnBooking || !booking.merchant_id) return;
    let cancelled = false;
    (async () => {
      const { data } = await getMerchantById(booking.merchant_id);
      if (!cancelled) {
        const enabled = !!(data?.off_session_payments || data?.deposits_enabled);
        setMerchantOffSessionEnabled(enabled);
      }
    })();
    return () => { cancelled = true; };
  }, [isMerchantAdminOnBooking, booking.merchant_id]);

  const loadRefundHistory = async () => {
    setLoadingRefunds(true);
    const { data, error } = await getRefundHistory(booking.id);
    if (data) {
      setRefundHistory(data);
    }
    if (error) {
      console.error('Failed to load refund history:', error);
    }
    setLoadingRefunds(false);
  };

  const loadTransactions = async () => {
    setLoadingTransactions(true);
    const { data, error } = await getPaymentTransactions(booking.id);
    if (data) {
      setTransactions(data);
    }
    if (error) {
      console.error('Failed to load payment transactions:', error);
    }
    setLoadingTransactions(false);
  };

  const formatPrice = (price: number | null) => {
    return formatCurrency(price, booking.currency);
  };

  // === Payment calculations ===
  // Gross booking payments: the larger of booking.amount_paid and the ledger,
  // damage charges excluded (see deriveRecordedBookingPaid). amountPaid adds the
  // damage charges back for the refundable / overpaid figures below.
  const bookingPaidGross = deriveRecordedBookingPaid(booking.amount_paid, transactions);
  const amountPaid = bookingPaidGross + transactions
    .filter(tx => tx.transaction_type === 'damage_charge')
    .reduce((sum, tx) => sum + tx.amount, 0);
  const totalRefunded = refundHistory.reduce((sum, r) => sum + Number(r.refund_amount), 0);
  // Confirmed booking-scope refunds are the only rows that move the booking's
  // payment ledger (damage refunds live on the separate Option B ledger,
  // pending_review rows await manual reconciliation) — mirrors
  // api/stripe/create-refund.js. Rows predating the reconciliation migration
  // (null scope/status) count as confirmed booking refunds.
  const bookingRefundedConfirmed = refundHistory.reduce((sum, r) => {
    const scope = r.scope ?? 'booking';
    const status = r.reconciliation_status ?? 'confirmed';
    return scope === 'booking' && status === 'confirmed' ? sum + Number(r.refund_amount) : sum;
  }, 0);
  // The subset of those that returned money for items removed from the booking
  // or for an overpayment (restores_balance). Their total reduction happens
  // separately — through the item edit — so they must be added back when
  // measuring what the customer owes, or they act as a credit against anything
  // added to the booking later. Mirrors deriveBalanceRestoringRefunded in
  // analytics.service.ts.
  const balanceRestoringRefunded = refundHistory.reduce((sum, r) => {
    const scope = r.scope ?? 'booking';
    const status = r.reconciliation_status ?? 'confirmed';
    return scope === 'booking' && status === 'confirmed' && r.restores_balance === true
      ? sum + Number(r.refund_amount)
      : sum;
  }, 0);
  const netBookingPaid = Math.max(0, bookingPaidGross - bookingRefundedConfirmed);

  // What the customer still OWES, measured against GROSS payments rather than
  // payments net of refunds. A refund reduces the obligation as well as the
  // collection; it does not turn refunded money back into a debt. A booking paid
  // in full and then 90% refunded is settled — the customer owes nothing and the
  // merchant kept the remaining 10%. Netting refunds off the paid side instead
  // would report the refund itself as outstanding and offer to email the customer
  // a demand for money that was deliberately given back.
  //
  // Refunds flagged restores_balance are the exception: the €10 battery light on
  // BK-2026-007218 was refunded and then removed, and when a day was added the
  // gross rule credited that €10 against the new day. Adding the flagged amount
  // back restores the obligation exactly for those refunds and nothing else.
  const amountDue = Math.max(0, booking.total_amount - bookingPaidGross + balanceRestoringRefunded);
  const hasOutstanding = amountDue > 0.01;
  const isFullyPaid = amountDue <= 0.01;

  // Refund state, kept separate from the debt above so the badge and copy can say
  // "paid, then refunded" instead of collapsing it into "partially paid".
  const hasBookingRefund = bookingRefundedConfirmed > 0.01;
  const isFullyRefunded = hasBookingRefund && netBookingPaid <= 0.01;
  // Overpayment is based on NET paid (gross minus refunds): a customer who
  // overpaid €108 and already got €97.20 back only has €10.80 still owed
  // to them, not the full gross delta. Previously this ignored refunds.
  const netPaid = amountPaid - totalRefunded;
  const isOverpaid = netPaid > booking.total_amount + 0.01;
  const overpaidAmount = isOverpaid ? netPaid - booking.total_amount : 0;
  const remainingRefundable = Math.max(0, amountPaid - totalRefunded);
  // Default for the "restores balance" checkbox on a booking-scope refund: an
  // overpaid booking is unambiguously getting an overpayment returned, so the
  // box starts ticked; otherwise it starts unticked (cancellation / goodwill is
  // the common case and must never turn into a debt). The operator can flip it
  // either way — the refund for a removed item can precede the removal.
  const refundRestoresBalance = refundRestoresBalanceChoice ?? isOverpaid;
  const hasStripePayment = !!booking.stripe_payment_intent_id || !!booking.stripe_charge_id;
  const hasPayPalPayment = !!booking.paypal_capture_id;
  const hasRefundablePayment = hasStripePayment || hasPayPalPayment;

  // Multi-payment awareness: compute Stripe-refundable vs non-Stripe portions
  const stripeTransactionTotal = getStripeTransactionTotal(transactions);
  const nonStripeTransactionTotal = getNonStripeTransactionTotal(transactions);
  const hasNonStripePayments = nonStripeTransactionTotal > 0.01;

  // Damage-charge ledger awareness (Option B): damage charges live in
  // payment_transactions but do not move booking.total_amount/amount_paid.
  const damageTransactions = transactions.filter(tx => tx.transaction_type === 'damage_charge');
  const bookingTransactions = transactions.filter(tx => tx.transaction_type !== 'damage_charge');
  const hasDamageCharges = damageTransactions.length > 0;
  const hasMixedTransactionTypes = hasDamageCharges && bookingTransactions.length > 0;
  const damagePaidTotal = damageTransactions.reduce((s, tx) => s + tx.amount, 0);
  const damageRefundedTotal = refundHistory
    .filter(r => damageTransactions.some(tx => tx.id === r.payment_transaction_id))
    .reduce((s, r) => s + Number(r.refund_amount), 0);
  const damageRemainingRefundable = Math.max(0, damagePaidTotal - damageRefundedTotal);

  // Off-session charge eligibility: needs at least one card-channel Stripe payment
  // (so a saved PaymentMethod is resolvable) AND merchant config enabled it
  // AND the caller has admin authority on this booking's merchant
  // AND the booking isn't fully refunded.
  //
  // isMerchantAdminOnBooking (declared above) = platform admin (cross-merchant)
  // OR merchant admin scoped to booking.merchant_id. Operators / managers /
  // viewers are intentionally excluded — off-session charges are higher-stakes
  // than refunds (no customer-in-the-loop, weaker chargeback defence) and
  // warrant ownership-level approval per merchant.
  const hasCardChannelStripePayment = transactions.some(tx =>
    tx.channel.startsWith('stripe_') && tx.stripe_payment_intent_id && tx.transaction_type !== 'damage_charge'
  ) || (!!booking.stripe_payment_intent_id && (booking as any).payment_method !== 'paypal');
  const isOffSessionEligible =
    isMerchantAdminOnBooking &&
    merchantOffSessionEnabled === true &&
    hasCardChannelStripePayment &&
    booking.payment_state !== 'refunded';
  // The max amount that can be refunded via the admin panel (Stripe only)
  const stripeRefundableAmount = transactions.length > 0
    ? Math.max(0, stripeTransactionTotal - totalRefunded)
    : remainingRefundable; // fallback for legacy bookings without transaction records
  // When refundScope === 'damage_charge', the cap is the damage-charge ledger
  // alone (independent of booking.total_amount). When 'booking_payment' on a
  // mixed booking, the cap excludes damage transactions / damage refunds.
  let effectiveRefundLimit: number;
  if (refundScope === 'damage_charge') {
    effectiveRefundLimit = damageRemainingRefundable;
  } else if (refundScope === 'booking_payment' && hasMixedTransactionTypes) {
    const bookingPaid = bookingTransactions.reduce((s, tx) => s + tx.amount, 0);
    const bookingRefunded = refundHistory
      .filter(r => bookingTransactions.some(tx => tx.id === r.payment_transaction_id))
      .reduce((s, r) => s + Number(r.refund_amount), 0);
    effectiveRefundLimit = Math.max(0, bookingPaid - bookingRefunded);
  } else if (isExternalRefund) {
    effectiveRefundLimit = remainingRefundable;
  } else {
    effectiveRefundLimit = (hasStripePayment && transactions.length > 0)
      ? Math.min(remainingRefundable, stripeRefundableAmount)
      : remainingRefundable;
  }

  // Calculate the actual refund amount based on mode and input
  // Capped to effectiveRefundLimit (Stripe-refundable portion for multi-payment bookings)
  const calculateRefundAmount = (): number => {
    const inputValue = parseFloat(refundInput);
    if (isNaN(inputValue) || inputValue <= 0) return 0;

    if (refundMode === 'percentage') {
      const pct = Math.min(inputValue, 100);
      // For damage-charge scope, percentage is of the damage total — booking
      // total isn't a meaningful base when refunding the damage ledger.
      const base = refundScope === 'damage_charge' ? damagePaidTotal : booking.total_amount;
      return Math.min(
        Math.round((base * pct / 100) * 100) / 100,
        effectiveRefundLimit
      );
    } else {
      return Math.min(inputValue, effectiveRefundLimit);
    }
  };

  const calculatedRefundAmount = calculateRefundAmount();

  const isRefundValid = () => {
    const inputValue = parseFloat(refundInput);
    if (isNaN(inputValue) || inputValue <= 0) return false;
    if (refundMode === 'percentage' && inputValue > 100) return false;
    if (calculatedRefundAmount <= 0) return false;
    if (calculatedRefundAmount > effectiveRefundLimit + 0.01) return false;
    // Mixed-type bookings require an explicit scope choice — without it, LIFO
    // would silently refund the wrong transaction.
    if (hasMixedTransactionTypes && refundScope === null) return false;
    return true;
  };

  // === Handlers ===

  // Reconcile the subtotal used for discount recalculations against the actual
  // line items. Some bookings (notably ones created on the online booking site)
  // have been persisted with an inconsistent subtotal — e.g. subtotal = 0 while
  // the line items are worth €78.90 — because a full-order discount was baked in
  // at creation. Trusting the stored subtotal here would then recompute the total
  // as 0 when a discount is applied/removed, wiping out the real order value and
  // leaving the booking looking "overpaid". Deriving the subtotal from the line
  // items (the same way NewBookingFlow / handleItemsChange compute it) makes these
  // handlers self-healing: the stored subtotal is only used as a fallback when no
  // line items are available on the loaded booking.
  const getReconciledSubtotal = (): number => {
    const items = (booking as any).booking_items;
    if (Array.isArray(items) && items.length > 0) {
      return items.reduce(
        (sum: number, item: any) => sum + (Number(item.total_price) || 0),
        0
      );
    }
    return booking.subtotal;
  };

  const handleApplyDiscountCode = async () => {
    if (!discountCodeInput.trim()) {
      setDiscountError(t('bookingFinancial.pleaseEnterDiscountCode'));
      return;
    }

    setValidating(true);
    setDiscountError(null);

    const newSubtotal = getReconciledSubtotal();
    const orderAmount = newSubtotal + (booking.delivery_price || 0);

    const result = await validateDiscountCode({
      code: discountCodeInput.trim(),
      merchantId: merchantId!,
      orderAmount,
      productTypeIds: []
    });

    if (!result.valid || result.error) {
      setDiscountError(result.error || t('bookingFinancial.invalidDiscountCode'));
      setValidating(false);
      return;
    }

    const newTotalDiscount = result.discount;

    const { totalTax: newTotalTax, totalAmount: newTotalAmount } = computeBookingTotals({
      subtotal: newSubtotal,
      deliveryPrice: booking.delivery_price,
      totalDiscount: newTotalDiscount,
      taxRate: booking.tax_rate,
      taxIncluded: booking.tax_included,
    });

    onChange({
      discount_code_id: result.discountCodeId || null,
      discount_code_used: discountCodeInput.trim().toUpperCase(),
      // Persist the reconciled subtotal so a previously-inconsistent value can't
      // continue to corrupt future recalculations.
      subtotal: newSubtotal,
      total_discount: newTotalDiscount,
      total_tax: newTotalTax,
      total_amount: newTotalAmount,
    });

    setDiscountCodeInput('');
    setValidating(false);
  };

  const handleRemoveDiscountCode = () => {
    const newSubtotal = getReconciledSubtotal();

    const { totalTax: newTotalTax, totalAmount: newTotalAmount } = computeBookingTotals({
      subtotal: newSubtotal,
      deliveryPrice: booking.delivery_price,
      totalDiscount: 0,
      taxRate: booking.tax_rate,
      taxIncluded: booking.tax_included,
    });

    onChange({
      discount_code_id: null,
      discount_code_used: null,
      // Persist the reconciled subtotal so removing a discount restores the real
      // order value even when the stored subtotal was 0 / inconsistent.
      subtotal: newSubtotal,
      total_discount: 0,
      total_tax: newTotalTax,
      total_amount: newTotalAmount,
    });
  };

  const handleSendPaymentLink = async () => {
    setSendingPaymentLink(true);
    setPaymentLinkUrl(null);

    const { data, error } = await createPaymentLink({
      bookingId: booking.id,
      amount: amountDue,
    });

    if (error) {
      showToast(`${t('bookingFinancial.failedToCreatePaymentLink')}: ${error}`, 'error', 6000);
    } else if (data) {
      setPaymentLinkUrl(data.checkoutUrl);
      showToast(t('bookingFinancial.paymentLinkCreated'), 'success');
    }

    setSendingPaymentLink(false);
  };

  const handleSendPaymentEmail = async () => {
    setSendingPaymentEmail(true);

    const { error } = await sendPaymentEmail({
      bookingId: booking.id,
      endDate: booking.end_date,
      bookingState: booking.booking_state,
      paymentState: booking.payment_state,
      language: booking.language || 'en',
    });

    if (error) {
      showToast(`${t('bookingFinancial.failedToSendPaymentEmail')}: ${error}`, 'error', 6000);
    } else {
      showToast(t('bookingFinancial.paymentEmailSent'), 'success');
    }

    setSendingPaymentEmail(false);
  };

  const handleRecordManualPayment = async () => {
    const amount = parseFloat(manualPaymentAmount);
    if (isNaN(amount) || amount <= 0) return;

    setRecordingPayment(true);

    const { error } = await recordManualPayment({
      bookingId: booking.id,
      amount,
      channel: manualPaymentChannel,
      notes: manualPaymentNotes.trim() || undefined,
      userId: user?.id,
    });

    if (error) {
      showToast(`${t('bookingFinancial.failedToRecordPayment')}: ${error}`, 'error', 6000);
    } else {
      showToast(`${t('bookingFinancial.paymentOfRecorded', { amount: formatPrice(amount) })}`, 'success');
      setManualPaymentAmount('');
      setManualPaymentNotes('');
      setShowPaymentForm(false);
      await loadTransactions();
      if (onPaymentRecorded) {
        onPaymentRecorded();
      }
    }

    setRecordingPayment(false);
  };

  const handleRefundSubmit = () => {
    if (!isRefundValid()) return;
    setShowConfirmModal(true);
  };

  const handleConfirmOffSessionCharge = async () => {
    const amount = parseFloat(offSessionAmount);
    if (isNaN(amount) || amount <= 0) return;
    if (!offSessionReason.trim()) return;

    setChargingOffSession(true);
    setOffSessionRequiresAction(null);

    const { data, error } = await chargeOffSession({
      bookingId: booking.id,
      amount,
      reason: offSessionReason.trim(),
      clientNonce: crypto.randomUUID(),
    });

    if (error) {
      showToast(`${t('bookingFinancial.offSessionChargeFailed', 'Off-session charge failed')}: ${error}`, 'error', 8000);
    } else if (data?.status === 'requires_action') {
      setOffSessionRequiresAction({
        paymentIntentId: data.paymentIntentId,
        message: data.message,
      });
      showToast(t('bookingFinancial.offSessionRequiresAction', 'Customer authentication required — see details below'), 'warning', 8000);
    } else if (data?.status === 'succeeded') {
      showToast(`${t('bookingFinancial.offSessionChargeSucceeded', 'Charged saved card')}: ${formatPrice(amount)}`, 'success');
      setOffSessionAmount('');
      setOffSessionReason('');
      setShowOffSessionForm(false);
      await loadTransactions();
      if (onPaymentRecorded) onPaymentRecorded();
    }

    setChargingOffSession(false);
  };

  const handleConfirmRefund = async () => {
    setShowConfirmModal(false);
    setProcessingRefund(true);

    const { data, error } = await processRefund({
      bookingId: booking.id,
      amount: calculatedRefundAmount,
      refundType: refundMode,
      refundValue: parseFloat(refundInput),
      reason: refundReason.trim() || undefined,
      userId: user?.id,
      ...(isExternalRefund ? { mode: 'bookkeeping' as const } : {}),
      ...(refundScope ? { transactionType: refundScope } : {}),
      ...(refundScope !== 'damage_charge' && refundRestoresBalance ? { restoresBalance: true } : {}),
    });

    if (error) {
      showToast(`${t('bookingFinancial.refundFailed')}: ${error}`, 'error', 6000);
      // Partial failure: some chunks may have succeeded, reload history
      if (data?.partialFailure) {
        await loadRefundHistory();
      }
    } else {
      const label = data?.isBookkeepingOnly ? t('bookingFinancial.bookkeepingRefund') : t('bookingFinancial.refund');
      showToast(`${label} ${t('bookingFinancial.ofProcessed', { amount: formatPrice(calculatedRefundAmount) })}`, 'success');
      setRefundInput('');
      setRefundReason('');
      setIsExternalRefund(false);
      setRefundScope(null);
      setRefundRestoresBalanceChoice(null);
      await loadRefundHistory();
      if (onRefundProcessed) {
        onRefundProcessed();
      }
    }

    setProcessingRefund(false);
  };

  return (
    <div className="space-y-lg overflow-hidden">
      {/* Discount Code Section */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-md flex items-center gap-xs">
          <Tag className="w-5 h-5 text-blue" />
          {t('bookingFinancial.discountCode')}
        </h3>

        {booking.discount_code_used ? (
          <div className="p-md bg-success/10 border border-success rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-xs mb-xs">
                  <Tag className="w-4 h-4 text-success" />
                  <span className="font-semibold text-success">
                    {t('bookingFinancial.codeApplied')}: {booking.discount_code_used}
                  </span>
                </div>
                <div className="text-sm text-secondary">
                  {t('bookingFinancial.discount')}: {formatPrice(booking.total_discount)}
                </div>
              </div>
              {!disabled && (
                <button
                  onClick={handleRemoveDiscountCode}
                  className="btn btn-ghost text-danger"
                >
                  <X className="w-4 h-4" />
                  {t('bookingFinancial.remove')}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              {t('bookingFinancial.applyDiscountCode')}
            </label>
            <div className="flex gap-md">
              <div className="flex-1">
                <input
                  type="text"
                  value={discountCodeInput}
                  onChange={(e) => {
                    setDiscountCodeInput(e.target.value.toUpperCase());
                    setDiscountError(null);
                  }}
                  disabled={disabled || validating}
                  className="input w-full"
                  placeholder={t('bookingFinancial.enterCodePlaceholder')}
                />
                {discountError && (
                  <p className="text-xs text-danger mt-xs">{discountError}</p>
                )}
              </div>
              <button
                onClick={handleApplyDiscountCode}
                disabled={disabled || validating || !discountCodeInput.trim()}
                className="btn btn-primary"
              >
                {validating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('bookingFinancial.validating')}
                  </>
                ) : (
                  t('bookingFinancial.apply')
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Calculated Totals */}
      <div className="border-t border-primary pt-lg">
        <h3 className="text-lg font-semibold text-primary mb-md flex items-center gap-xs">
          <Receipt className="w-5 h-5 text-blue" data-testid="booking-financial-summary-icon" />
          {t('bookingFinancial.financialSummary')}
        </h3>
        <div className="bg-tertiary rounded-lg p-md sm:p-lg">
          <div className="space-y-md">
            <div className="flex justify-between text-sm">
              <span className="text-secondary">{t('bookingFinancial.subtotal')}:</span>
              <span className="font-medium text-primary">{formatPrice(booking.subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-secondary">{t('bookingFinancial.delivery')}:</span>
              <span className="font-medium text-primary">{formatPrice(booking.delivery_price)}</span>
            </div>
            {(booking.total_discount ?? 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-secondary">{t('bookingFinancial.discount')} ({booking.discount_code_used}):</span>
                <span className="font-medium text-success">-{formatPrice(booking.total_discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-secondary">
                {t('bookingFinancial.tax')} {booking.tax_rate ? `(${(booking.tax_rate * 100).toFixed(2)}%)` : ''}:
              </span>
              <span className="font-medium text-primary">{formatPrice(booking.total_tax)}</span>
            </div>
            {booking.tax_included && (
              <div className="text-xs text-secondary">{t('bookingFinancial.taxIncluded')}</div>
            )}
            <div className="border-t border-primary pt-md mt-md">
              <div className="flex justify-between">
                <span className="text-lg font-semibold text-primary">{t('bookingFinancial.total')}:</span>
                <span className="text-2xl font-bold text-primary">
                  {formatPrice(booking.total_amount)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Status & Breakdown */}
      <div className="border-t border-primary pt-lg">
        <h3 className="text-lg font-semibold text-primary mb-md flex items-center gap-xs">
          <CreditCard className="w-5 h-5 text-blue" />
          {t('bookingFinancial.paymentStatus')}
        </h3>

        {/* Payment Progress Bar */}
        <div className="bg-tertiary rounded-lg p-md sm:p-lg mb-md">
          <div className="flex flex-wrap items-center justify-between gap-sm sm:gap-md mb-md">
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-sm sm:gap-lg">
              <div>
                <div className="text-xs text-secondary">{t('bookingFinancial.totalAmount')}</div>
                <div className="font-semibold text-primary">{formatPrice(booking.total_amount)}</div>
              </div>
              <div>
                <div className="text-xs text-secondary">{t('bookingFinancial.amountPaid')}</div>
                <div className={`font-semibold ${isFullyPaid ? 'text-success' : amountPaid > 0 ? 'text-warning' : 'text-secondary'}`}>
                  {formatPrice(amountPaid)}
                </div>
              </div>
              {totalRefunded > 0 && (
                <div>
                  <div className="text-xs text-secondary">{t('bookingFinancial.refunded')}</div>
                  <div className="font-semibold text-danger">{formatPrice(totalRefunded)}</div>
                </div>
              )}
              <div>
                <div className="text-xs text-secondary">
                  {isOverpaid ? t('bookingFinancial.overpaid') : t('bookingFinancial.outstanding')}
                </div>
                <div className={`font-semibold ${isOverpaid ? 'text-warning' : hasOutstanding ? 'text-danger' : 'text-success'}`}>
                  {isOverpaid ? `+${formatPrice(overpaidAmount)}` : formatPrice(amountDue)}
                </div>
              </div>
            </div>

            {/* Payment state badge */}
            {/* A refund only earns the "refunded" badge while nothing is owed. Once
                money is outstanding again (restores_balance refund, then the booking
                grew) the call to action wins: the refund is still stated in the
                outstanding copy and the refund tile. */}
            <span className={`inline-flex items-center gap-xs px-sm py-xs text-xs font-medium rounded ${
              isFullyRefunded && !hasOutstanding
                ? 'bg-tertiary text-secondary border border-primary'
                : hasBookingRefund && !hasOutstanding
                  ? 'bg-warning/10 text-warning border border-warning/20'
                  : isFullyPaid
                    ? 'bg-success/10 text-success border border-success/20'
                    : bookingPaidGross > 0
                      ? 'bg-warning/10 text-warning border border-warning/20'
                      : 'bg-tertiary text-secondary border border-primary'
            }`}>
              {isFullyRefunded && !hasOutstanding
                ? t('bookingFinancial.refunded')
                : hasBookingRefund && !hasOutstanding
                  ? t('bookingFinancial.partiallyRefunded')
                  : isFullyPaid
                    ? t('bookingFinancial.paid')
                    : bookingPaidGross > 0
                      ? t('bookingFinancial.partiallyPaid')
                      : booking.payment_state === 'open_pay_later'
                        ? t('bookingFinancial.payLater')
                        : t('bookingFinancial.open')}
            </span>
          </div>

          {/* Visual progress bar */}
          {booking.total_amount > 0 && (
            <div className="w-full bg-primary/20 rounded-full h-2">
              {/* Tracks collection against the obligation, so a booking paid in
                  full reads 100% even after a refund. The refund is carried by the
                  badge and the "refunded" tile, not by rewinding the bar. */}
              <div
                className={`h-2 rounded-full transition-all ${hasBookingRefund ? 'bg-warning' : isFullyPaid ? 'bg-success' : bookingPaidGross > 0 ? 'bg-warning' : 'bg-primary/20'}`}
                style={{ width: `${Math.min(100, (bookingPaidGross / booking.total_amount) * 100)}%` }}
              />
            </div>
          )}
        </div>

        {/* Outstanding Amount Warning + Actions */}
        {hasOutstanding && canManagePayments && (
          <div className="p-sm sm:p-md bg-warning/10 border-l-4 border-warning rounded-lg mb-md">
            <div className="flex items-start gap-sm sm:gap-md">
              <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-warning mb-xs text-sm sm:text-base">
                  {t('bookingFinancial.outstandingBalance')}: {formatPrice(amountDue)}
                </h4>
                <p className="text-xs sm:text-sm text-secondary mb-md">
                  {bookingPaidGross <= 0.01
                    ? t('bookingFinancial.noPaymentsRecorded')
                    : hasBookingRefund
                      // State both facts. "Paid X of Y" alone reads as though the
                      // refunded amount is still owed.
                      ? t('bookingFinancial.customerHasPaidWithRefund', {
                          paid: formatPrice(bookingPaidGross),
                          total: formatPrice(booking.total_amount),
                          refunded: formatPrice(bookingRefundedConfirmed),
                        })
                      : t('bookingFinancial.customerHasPaid', {
                          paid: formatPrice(bookingPaidGross),
                          total: formatPrice(booking.total_amount),
                        })
                  }
                </p>

                <div className="flex flex-col sm:flex-row flex-wrap gap-sm">
                  {/* Send Payment Email Button — emails the customer the
                      manage-booking link (Stripe + PayPal) via Make.com */}
                  <button
                    onClick={handleSendPaymentEmail}
                    disabled={sendingPaymentEmail}
                    className="btn btn-primary text-sm"
                  >
                    {sendingPaymentEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('bookingFinancial.sending')}
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        {t('bookingFinancial.sendPaymentEmail')} ({formatPrice(amountDue)})
                      </>
                    )}
                  </button>

                  {/* Send Payment Link Button — Stripe Checkout fallback (card only) */}
                  <button
                    onClick={handleSendPaymentLink}
                    disabled={sendingPaymentLink}
                    className="btn btn-secondary text-sm"
                  >
                    {sendingPaymentLink ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('bookingFinancial.creating')}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        {t('bookingFinancial.createPaymentLink')}
                      </>
                    )}
                  </button>

                  {/* Record Manual Payment Button */}
                  <button
                    onClick={() => {
                      setManualPaymentAmount(String(amountDue.toFixed(2)));
                      setShowPaymentForm(true);
                    }}
                    className="btn btn-secondary text-sm"
                  >
                    <Banknote className="w-4 h-4" />
                    {t('bookingFinancial.recordPayment')}
                  </button>
                </div>

                {/* Payment link URL display */}
                {paymentLinkUrl && (
                  <div className="mt-md p-sm bg-primary/5 border border-primary/20 rounded-lg">
                    <div className="flex items-center gap-xs mb-xs">
                      <ExternalLink className="w-3 h-3 text-blue" />
                      <span className="text-xs font-medium text-primary">{t('bookingFinancial.paymentLinkCreatedLabel')}</span>
                    </div>
                    <a
                      href={paymentLinkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue hover:underline break-all"
                    >
                      {paymentLinkUrl}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Overpaid Warning */}
        {isOverpaid && (
          <div className="p-md bg-warning/10 border-l-4 border-warning rounded-lg mb-md">
            <div className="flex items-start gap-md">
              <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-warning mb-xs">{t('bookingFinancial.overpaymentDetected')}</h4>
                <p className="text-sm text-secondary">
                  {t('bookingFinancial.overpaymentMessage', { paid: formatPrice(amountPaid), total: formatPrice(booking.total_amount), refund: formatPrice(overpaidAmount) })}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Off-Session Damage Charge — admin-only, gated on saved-card eligibility.
            Charges a saved card for damages/late fees/etc. without the customer
            in the loop. Damage charges live on a separate ledger (Option B):
            they do NOT mutate booking.total_amount/amount_paid/payment_state. */}
        {isOffSessionEligible && !showOffSessionForm && (
          <div className="flex flex-wrap items-center justify-between gap-sm p-md bg-tertiary border border-primary/20 rounded-lg mb-md">
            <div className="flex items-center gap-sm">
              <Wrench className="w-4 h-4 text-warning flex-shrink-0" />
              <div>
                <div className="text-sm font-medium text-primary">
                  {t('bookingFinancial.chargeSavedCardTitle', 'Charge saved card (off-session)')}
                </div>
                <div className="text-xs text-secondary">
                  {t('bookingFinancial.chargeSavedCardSubtitle', 'For damages, late returns, cleaning fees, or other amounts owed under the rental agreement.')}
                </div>
              </div>
            </div>
            <button
              onClick={() => {
                setOffSessionAmount('');
                setOffSessionReason('');
                setOffSessionRequiresAction(null);
                setShowOffSessionForm(true);
              }}
              className="btn btn-secondary text-sm"
            >
              <CreditCard className="w-4 h-4" />
              {t('bookingFinancial.chargeSavedCard', 'Charge saved card')}
            </button>
          </div>
        )}

        {showOffSessionForm && isOffSessionEligible && (
          <div className="bg-tertiary rounded-lg p-md sm:p-lg mb-md border border-warning/30">
            <h4 className="text-sm font-semibold text-primary mb-md flex items-center gap-xs">
              <Wrench className="w-4 h-4 text-warning" />
              {t('bookingFinancial.chargeSavedCardTitle', 'Charge saved card (off-session)')}
            </h4>

            <div className="p-sm bg-warning/10 border border-warning/30 rounded-lg mb-md">
              <div className="flex items-start gap-sm">
                <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm text-secondary">
                  {t('bookingFinancial.chargeSavedCardWarning', 'This charges the customer\'s saved card without their direct involvement. Use only for amounts owed under the rental agreement (damages, late fees, cleaning fees). Charges substantially larger than the original booking may be declined and require customer authentication.')}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-md mb-md">
              <div>
                <label className="block text-sm font-medium text-primary mb-xs">{t('bookingFinancial.amount')}</label>
                <input
                  type="number"
                  value={offSessionAmount}
                  onChange={(e) => setOffSessionAmount(e.target.value)}
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  className="input w-full"
                  disabled={chargingOffSession}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-xs">
                  {t('bookingFinancial.reason')} <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={offSessionReason}
                  onChange={(e) => setOffSessionReason(e.target.value)}
                  maxLength={500}
                  placeholder={t('bookingFinancial.offSessionReasonPlaceholder', 'e.g. Damage to bike frame, lock missing on return')}
                  className="input w-full"
                  disabled={chargingOffSession}
                />
              </div>
            </div>

            {offSessionRequiresAction && (
              <div className="p-sm bg-warning/10 border border-warning/30 rounded-lg mb-md">
                <div className="flex items-start gap-sm">
                  <AlertCircle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                  <div className="text-xs sm:text-sm text-secondary">
                    <div className="font-medium text-warning mb-xs">
                      {t('bookingFinancial.offSessionRequiresActionTitle', 'Customer authentication required')}
                    </div>
                    <p>{offSessionRequiresAction.message}</p>
                    {offSessionRequiresAction.paymentIntentId && (
                      <p className="font-mono text-xs mt-xs break-all">
                        PaymentIntent: {offSessionRequiresAction.paymentIntentId}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-sm">
              <button
                onClick={handleConfirmOffSessionCharge}
                disabled={
                  chargingOffSession ||
                  !offSessionAmount ||
                  parseFloat(offSessionAmount) <= 0 ||
                  !offSessionReason.trim()
                }
                className="btn btn-primary"
              >
                {chargingOffSession ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('bookingFinancial.charging', 'Charging…')}
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4" />
                    {t('bookingFinancial.confirmChargeSavedCard', 'Charge saved card')}
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowOffSessionForm(false);
                  setOffSessionRequiresAction(null);
                }}
                className="btn btn-secondary"
                disabled={chargingOffSession}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Manual Payment Form */}
        {showPaymentForm && canManagePayments && (
          <div className="bg-tertiary rounded-lg p-md sm:p-lg mb-md">
            <h4 className="text-sm font-semibold text-primary mb-md flex items-center gap-xs">
              <Wallet className="w-4 h-4 text-blue" />
              {t('bookingFinancial.recordManualPayment')}
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-md mb-md">
              <div>
                <label className="block text-sm font-medium text-primary mb-xs">{t('bookingFinancial.amount')}</label>
                <input
                  type="number"
                  value={manualPaymentAmount}
                  onChange={(e) => setManualPaymentAmount(e.target.value)}
                  min="0.01"
                  step="0.01"
                  placeholder="0.00"
                  className="input w-full"
                  disabled={recordingPayment}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-xs">{t('bookingFinancial.paymentChannel')}</label>
                <select
                  value={manualPaymentChannel}
                  onChange={(e) => setManualPaymentChannel(e.target.value)}
                  className="input w-full"
                  disabled={recordingPayment}
                >
                  {PAYMENT_CHANNELS.filter(c => !c.value.startsWith('stripe_')).map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                  {PAYMENT_CHANNELS.filter(c => c.value.startsWith('stripe_')).map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-md">
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('bookingFinancial.notes')} <span className="text-secondary font-normal">({t('bookingFinancial.optional')})</span>
              </label>
              <input
                type="text"
                value={manualPaymentNotes}
                onChange={(e) => setManualPaymentNotes(e.target.value)}
                placeholder={t('bookingFinancial.notesPlaceholder')}
                className="input w-full"
                disabled={recordingPayment}
              />
            </div>

            <div className="flex gap-sm">
              <button
                onClick={handleRecordManualPayment}
                disabled={recordingPayment || !manualPaymentAmount || parseFloat(manualPaymentAmount) <= 0}
                className="btn btn-primary"
              >
                {recordingPayment ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('bookingFinancial.recording')}
                  </>
                ) : (
                  <>
                    <DollarSign className="w-4 h-4" />
                    {t('bookingFinancial.recordPayment')}
                  </>
                )}
              </button>
              <button
                onClick={() => setShowPaymentForm(false)}
                className="btn btn-secondary"
                disabled={recordingPayment}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Nothing outstanding + Record More.
            When refunds exist this is NOT "fully paid" — the customer owes nothing
            but money went back, so it is shown neutrally rather than as success.
            The record-payment action stays here either way: it is what makes
            re-collecting after a refund issued in error possible now that a refund
            no longer shows up as an outstanding balance. */}
        {isFullyPaid && canManagePayments && !showPaymentForm && (
          <div className={`flex flex-wrap items-center justify-between gap-sm p-md rounded-lg mb-md ${
            hasBookingRefund
              ? 'bg-tertiary border border-primary'
              : 'bg-success/10 border border-success/20'
          }`}>
            <div className="flex items-center gap-sm">
              <DollarSign className={`w-4 h-4 flex-shrink-0 ${hasBookingRefund ? 'text-secondary' : 'text-success'}`} />
              <span className={`text-sm font-medium ${hasBookingRefund ? 'text-secondary' : 'text-success'}`}>
                {hasBookingRefund
                  ? t('bookingFinancial.noOutstandingAfterRefund', { refunded: formatPrice(bookingRefundedConfirmed) })
                  : t('bookingFinancial.fullyPaid')}
              </span>
            </div>
            <button
              onClick={() => {
                setManualPaymentAmount('');
                setShowPaymentForm(true);
              }}
              className="btn btn-ghost text-sm"
            >
              <Plus className="w-3 h-3" />
              {t('bookingFinancial.recordAdditionalPayment')}
            </button>
          </div>
        )}

        {/* Payment Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
          <div className="p-md bg-tertiary rounded-lg">
            <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingFinancial.paymentState')}</label>
            <div className="text-primary font-semibold">
              {booking.payment_state.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
            </div>
          </div>
          <div className="p-md bg-tertiary rounded-lg">
            <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingFinancial.currency')}</label>
            <div className="text-primary">{booking.currency || 'USD'}</div>
          </div>
          {/* Payment Method Badge */}
          {(booking as any).payment_method && (
            <div className="p-md bg-tertiary rounded-lg">
              <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingFinancial.paymentMethod', 'Payment Method')}</label>
              <div className="flex items-center gap-sm">
                <span className={`inline-flex items-center px-sm py-xs rounded-full text-xs font-medium ${
                  (booking as any).payment_method === 'paypal'
                    ? 'bg-blue/10 text-blue'
                    : (booking as any).payment_method === 'pay_later'
                      ? 'bg-warning/10 text-warning'
                      : 'bg-purple-100 text-purple-700'
                }`}>
                  {(booking as any).payment_method === 'paypal' ? 'PayPal' : (booking as any).payment_method === 'pay_later' ? t('bookingFinancial.payLater', 'Pay Later') : 'Stripe'}
                </span>
              </div>
            </div>
          )}
          {booking.stripe_payment_intent_id && (
            <div className="p-md bg-tertiary rounded-lg">
              <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingFinancial.stripePaymentIntent')}</label>
              <div className="text-primary text-sm font-mono break-all">
                {booking.stripe_payment_intent_id}
              </div>
            </div>
          )}
          {booking.stripe_charge_id && (
            <div className="p-md bg-tertiary rounded-lg">
              <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingFinancial.stripeChargeId')}</label>
              <div className="text-primary text-sm font-mono break-all">
                {booking.stripe_charge_id}
              </div>
            </div>
          )}
          {(booking as any).paypal_order_id && (
            <div className="p-md bg-tertiary rounded-lg">
              <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingFinancial.paypalOrderId', 'PayPal Order ID')}</label>
              <div className="text-primary text-sm font-mono break-all">
                {(booking as any).paypal_order_id}
              </div>
            </div>
          )}
          {(booking as any).paypal_capture_id && (
            <div className="p-md bg-tertiary rounded-lg">
              <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingFinancial.paypalCaptureId', 'PayPal Capture ID')}</label>
              <div className="text-primary text-sm font-mono break-all">
                {(booking as any).paypal_capture_id}
              </div>
            </div>
          )}
          {booking.download_receipt_link && (
            <div className="p-md bg-tertiary rounded-lg md:col-span-2">
              <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingFinancial.receiptLink')}</label>
              <a
                href={booking.download_receipt_link}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue hover:underline text-sm break-all"
              >
                {booking.download_receipt_link}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Payment Transaction History */}
      <div className="border-t border-primary pt-lg">
        <h3 className="text-lg font-semibold text-primary mb-md flex items-center gap-xs">
          <Clock className="w-5 h-5 text-blue" />
          {t('bookingFinancial.paymentHistory')}
        </h3>

        {loadingTransactions ? (
          <div className="flex items-center gap-sm text-secondary text-sm py-md">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('bookingFinancial.loadingPaymentHistory')}
          </div>
        ) : transactions.length === 0 ? (
          <p className="text-sm text-secondary py-md">{t('bookingFinancial.noPaymentsRecorded')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-primary">
                  <th className="text-left py-sm pr-md text-secondary font-medium">{t('bookingFinancial.date')}</th>
                  <th className="text-left py-sm pr-md text-secondary font-medium">{t('bookingFinancial.channel')}</th>
                  <th className="text-right py-sm pr-md text-secondary font-medium">{t('bookingFinancial.amount')}</th>
                  <th className="text-left py-sm pr-md text-secondary font-medium">{t('bookingFinancial.reference')}</th>
                  <th className="text-left py-sm text-secondary font-medium">{t('bookingFinancial.notes')}</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.id} className="border-b border-primary/50">
                    <td className="py-sm pr-md text-primary whitespace-nowrap">
                      {formatDateTimeCompact(tx.created_at)}
                    </td>
                    <td className="py-sm pr-md">
                      <div className="flex flex-wrap items-center gap-xs">
                        <span className="inline-flex items-center gap-xs px-sm py-xs text-xs font-medium bg-blue/10 text-blue rounded">
                          {getChannelLabel(tx.channel)}
                        </span>
                        {tx.transaction_type === 'damage_charge' && (
                          <span className="inline-flex items-center gap-xs px-sm py-xs text-xs font-medium bg-warning/10 text-warning rounded">
                            <Wrench className="w-3 h-3" />
                            {t('bookingFinancial.damageBadge', 'Damage')}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-sm pr-md text-right font-medium text-success">
                      +{formatPrice(tx.amount)}
                    </td>
                    <td className="py-sm pr-md font-mono text-xs text-secondary break-all">
                      {tx.stripe_payment_intent_id || tx.stripe_checkout_session_id || '\u2014'}
                    </td>
                    <td className="py-sm text-secondary">
                      {tx.notes || '\u2014'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deposit Section — only visible when the booking has a deposit */}
      {(booking as any).deposit_payment_intent_id && (() => {
        const b = booking as any;
        const depositStatus: string = b.deposit_status || 'unknown';
        const depositAmount: number = b.deposit_amount || 0;
        const depositCaptured: number = b.deposit_captured_amount || 0;
        const depositAuthorizedAt: string | null = b.deposit_authorized_at;
        const depositFailureReason: string | null = b.deposit_failure_reason;
        const authExpiryMs = depositAuthorizedAt
          ? new Date(depositAuthorizedAt).getTime() + 7 * 24 * 60 * 60 * 1000
          : null;
        const now = Date.now();
        const expiresInMs = authExpiryMs ? authExpiryMs - now : null;
        const isExpiringSoon = depositStatus === 'authorized' && expiresInMs !== null && expiresInMs < 48 * 60 * 60 * 1000 && expiresInMs > 0;

        const formatCents = (cents: number) => formatPrice(cents / 100);

        const statusBadge: Record<string, { cls: string; label: string }> = {
          authorized: { cls: 'badge-warning', label: t('deposit.authorized') },
          captured: { cls: 'badge-success', label: t('deposit.capturedStatus') },
          released: { cls: 'badge-info', label: t('deposit.released') },
          refunded: { cls: 'badge-info', label: t('deposit.refundedStatus') },
          expired: { cls: 'badge', label: t('deposit.expired') },
          failed: { cls: 'badge-danger', label: t('deposit.failed') },
        };
        const badge = statusBadge[depositStatus] || { cls: 'badge', label: depositStatus };

        const openDepositAction = (action: DepositAction) => {
          setDepositAction(action);
          if (action === 'capture') {
            setDepositAmountInput(String(depositAmount));
          } else if (action === 'refund') {
            setDepositAmountInput(String(depositCaptured || depositAmount));
          } else {
            setDepositAmountInput('');
          }
        };

        const handleDepositConfirm = async () => {
          if (!depositAction) return;
          setProcessingDeposit(true);
          const amountCents = depositAction !== 'release' ? parseInt(depositAmountInput, 10) : undefined;
          const { error } = await performDepositAction({
            bookingId: booking.id,
            action: depositAction,
            amount: amountCents,
            userId: user?.id,
          });
          setProcessingDeposit(false);
          setDepositAction(null);
          if (error) {
            showToast(error, 'error');
          } else {
            const msg = depositAction === 'release'
              ? t('deposit.releaseSucceeded')
              : depositAction === 'capture'
                ? t('deposit.captureSucceeded', { amount: formatCents(amountCents || depositAmount) })
                : t('deposit.refundSucceeded', { amount: formatCents(amountCents || depositCaptured) });
            showToast(msg, 'success');
            onPaymentRecorded?.();
          }
        };

        const depositAmountInt = parseInt(depositAmountInput, 10);
        const maxAmount = depositAction === 'capture' ? depositAmount : depositCaptured || depositAmount;
        const amountValid = depositAction === 'release' || (depositAmountInt > 0 && depositAmountInt <= maxAmount);

        const timeLeftLabel = expiresInMs !== null && expiresInMs > 0
          ? expiresInMs > 24 * 60 * 60 * 1000
            ? `in ${Math.ceil(expiresInMs / (24 * 60 * 60 * 1000))} days`
            : `in ${Math.ceil(expiresInMs / (60 * 60 * 1000))} hours`
          : null;

        return (
          <div className="border-t border-primary pt-lg">
            <h3 className="text-lg font-semibold text-primary mb-md flex items-center gap-xs">
              <Shield className="w-5 h-5 text-blue" />
              {t('deposit.title')}
            </h3>

            {/* Deposit summary */}
            <div className="bg-tertiary rounded-lg p-md mb-md">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-sm">
                <div>
                  <div className="text-xs text-secondary">{t('deposit.status')}</div>
                  <span className={`badge ${badge.cls} mt-xs`}>{badge.label}</span>
                </div>
                <div>
                  <div className="text-xs text-secondary">{t('deposit.amount')}</div>
                  <div className="font-semibold text-primary">{formatCents(depositAmount)}</div>
                </div>
                {depositCaptured > 0 && (
                  <div>
                    <div className="text-xs text-secondary">{t('deposit.captured')}</div>
                    <div className="font-semibold text-success">{formatCents(depositCaptured)}</div>
                  </div>
                )}
                {depositAuthorizedAt && (
                  <div>
                    <div className="text-xs text-secondary">{t('deposit.authorizedAt')}</div>
                    <div className="text-sm text-primary">{formatDateTimeCompact(depositAuthorizedAt)}</div>
                  </div>
                )}
                {authExpiryMs && depositStatus === 'authorized' && (
                  <div>
                    <div className="text-xs text-secondary">{t('deposit.expiresAt')}</div>
                    <div className="text-sm text-primary">{formatDateTimeCompact(new Date(authExpiryMs).toISOString())}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Warnings */}
            {isExpiringSoon && timeLeftLabel && (
              <div className="p-md bg-warning/10 border border-warning/20 rounded-lg mb-md flex items-start gap-sm">
                <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <p className="text-sm text-primary">{t('deposit.expiryWarning', { timeLeft: timeLeftLabel })}</p>
              </div>
            )}
            {depositStatus === 'expired' && (
              <div className="p-md bg-tertiary border border-primary rounded-lg mb-md flex items-start gap-sm">
                <AlertCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <p className="text-sm text-secondary">{t('deposit.expiredNotice')}</p>
              </div>
            )}
            {depositStatus === 'failed' && depositFailureReason && (
              <div className="p-md bg-danger/10 border border-danger/20 rounded-lg mb-md flex items-start gap-sm">
                <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
                <p className="text-sm text-danger">{t('deposit.failedNotice', { reason: depositFailureReason })}</p>
              </div>
            )}

            {/* Action buttons */}
            {canManagePayments && (
              <div className="flex flex-wrap gap-sm">
                {depositStatus === 'authorized' && (
                  <>
                    <button onClick={() => openDepositAction('release')} className="btn btn-secondary" disabled={processingDeposit}>
                      {t('deposit.releaseDeposit')}
                    </button>
                    <button onClick={() => openDepositAction('capture')} className="btn btn-primary" disabled={processingDeposit}>
                      {t('deposit.captureDeposit')}
                    </button>
                  </>
                )}
                {depositStatus === 'captured' && (
                  <button onClick={() => openDepositAction('refund')} className="btn btn-secondary" disabled={processingDeposit}>
                    <RotateCcw className="w-4 h-4" />
                    {t('deposit.refundDeposit')}
                  </button>
                )}
              </div>
            )}

            {/* Confirmation modal */}
            {depositAction && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                onClick={() => !processingDeposit && setDepositAction(null)}
              >
                <div
                  className="bg-primary rounded-xl shadow-2xl p-xl w-full max-w-md mx-md"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-lg font-bold text-primary mb-md flex items-center gap-xs">
                    <AlertCircle className="w-5 h-5 text-danger" />
                    {depositAction === 'release' && t('deposit.confirmRelease')}
                    {depositAction === 'capture' && t('deposit.confirmCapture')}
                    {depositAction === 'refund' && t('deposit.confirmRefund')}
                  </h3>

                  {/* Amount input for capture / refund */}
                  {depositAction !== 'release' && (
                    <div className="mb-md">
                      <label className="block text-sm font-medium text-secondary mb-xs">
                        {depositAction === 'capture' ? t('deposit.captureAmountLabel') : t('deposit.refundAmountLabel')}
                      </label>
                      <input
                        type="number"
                        className="input w-full"
                        value={depositAmountInput}
                        onChange={(e) => setDepositAmountInput(e.target.value)}
                        placeholder={depositAction === 'capture'
                          ? t('deposit.partialCapturePlaceholder', { amount: depositAmount })
                          : t('deposit.partialRefundPlaceholder', { amount: maxAmount })}
                        min={1}
                        max={maxAmount}
                        disabled={processingDeposit}
                      />
                      {depositAmountInt > maxAmount && (
                        <p className="text-xs text-danger mt-xs">
                          {depositAction === 'capture'
                            ? t('deposit.captureExceedsAuthorized', { max: maxAmount })
                            : t('deposit.refundExceedsCaptured', { max: maxAmount })}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Confirmation text */}
                  <div className="bg-tertiary rounded-lg p-md mb-md">
                    <p className="text-sm text-primary">
                      {depositAction === 'release' && t('deposit.releaseConfirmMessage', { amount: formatCents(depositAmount) })}
                      {depositAction === 'capture' && t('deposit.captureConfirmMessage', { amount: formatCents(depositAmountInt || depositAmount) })}
                      {depositAction === 'refund' && t('deposit.refundConfirmMessage', { amount: formatCents(depositAmountInt || maxAmount) })}
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex justify-end gap-md">
                    <button
                      onClick={() => setDepositAction(null)}
                      className="btn btn-secondary"
                      disabled={processingDeposit}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      onClick={handleDepositConfirm}
                      className={`btn ${depositAction === 'release' || depositAction === 'refund' ? 'btn-danger' : 'btn-primary'}`}
                      disabled={processingDeposit || !amountValid}
                    >
                      {processingDeposit ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t('deposit.processing')}
                        </>
                      ) : (
                        <>
                          {depositAction === 'release' && t('deposit.confirmRelease')}
                          {depositAction === 'capture' && t('deposit.confirmCapture')}
                          {depositAction === 'refund' && t('deposit.confirmRefund')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Refund Section */}
      <div className="border-t border-primary pt-lg">
        <h3 className="text-lg font-semibold text-primary mb-md flex items-center gap-xs">
          <RotateCcw className="w-5 h-5 text-blue" />
          {t('bookingFinancial.refund')}
        </h3>

        {/* Refund Summary Bar */}
        <div className="bg-tertiary rounded-lg p-sm sm:p-md mb-lg">
          <div className="flex flex-wrap items-center justify-between gap-sm sm:gap-md">
            <div className="grid grid-cols-2 sm:flex sm:items-center gap-sm sm:gap-lg">
              <div>
                <div className="text-xs text-secondary">{t('bookingFinancial.totalAmount')}</div>
                <div className="font-semibold text-primary">{formatPrice(booking.total_amount)}</div>
              </div>
              <div>
                <div className="text-xs text-secondary">{t('bookingFinancial.totalRefunded')}</div>
                <div className="font-semibold text-danger">{formatPrice(totalRefunded)}</div>
              </div>
              <div>
                <div className="text-xs text-secondary">{t('bookingFinancial.remainingRefundable')}</div>
                <div className="font-semibold text-primary">{formatPrice(remainingRefundable)}</div>
              </div>
              {hasNonStripePayments && hasStripePayment && effectiveRefundLimit < remainingRefundable - 0.01 && (
                <div>
                  <div className="text-xs text-secondary">{t('bookingFinancial.stripeRefundable', 'Stripe Refundable')}</div>
                  <div className="font-semibold text-blue">{formatPrice(effectiveRefundLimit)}</div>
                </div>
              )}
            </div>
            {!hasRefundablePayment && (
              <span className="inline-flex items-center gap-xs px-sm py-xs text-xs font-medium bg-warning/10 text-warning border border-warning/20 rounded">
                <BookOpen className="w-3 h-3" />
                {t('bookingFinancial.bookkeepingOnly')}
              </span>
            )}
          </div>
        </div>

        {/* PayPal refund notice — real refunds must be done via PayPal, but
            the "record external refund" toggle below lets admins log the
            refund in the ledger once it's been issued externally. */}
        {canManagePayments && remainingRefundable > 0 && hasPayPalPayment && !hasStripePayment && (
          <div className="p-md bg-blue/5 border border-blue/20 rounded-lg mb-lg">
            <div className="flex items-start gap-sm">
              <AlertCircle className="w-5 h-5 text-blue flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-primary mb-xs">
                  {t('bookingFinancial.paypalRefundNoticeTitle')}
                </p>
                <p className="text-sm text-secondary">
                  {t('bookingFinancial.paypalRefundInstructions', 'Process the refund in your PayPal dashboard, then tick "Already refunded externally" below to record it here.')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Non-Stripe payment notice — when booking has mixed payment methods */}
        {canManagePayments && remainingRefundable > 0 && hasNonStripePayments && hasStripePayment && (
          <div className="p-md bg-blue/5 border border-blue/20 rounded-lg mb-lg">
            <div className="flex items-start gap-sm">
              <AlertCircle className="w-5 h-5 text-blue flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-primary mb-xs">
                  {t('bookingFinancial.mixedPaymentNoticeTitle', 'Mixed payment methods detected')}
                </p>
                <p className="text-sm text-secondary">
                  {t('bookingFinancial.mixedPaymentNotice',
                    'This booking includes {{nonStripeAmount}} paid via non-Stripe methods (PayPal, cash, etc.). Only the Stripe portion ({{stripeAmount}} refundable) can be refunded from this panel. Non-Stripe amounts must be refunded externally.',
                    { nonStripeAmount: formatPrice(nonStripeTransactionTotal), stripeAmount: formatPrice(stripeRefundableAmount) }
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Refund Form */}
        {canManagePayments && (remainingRefundable > 0 || damageRemainingRefundable > 0) && (
          <div className="bg-tertiary rounded-lg p-md sm:p-lg mb-lg">
            {/* Bookkeeping-only notice (legacy bookings with no Stripe/PayPal records) */}
            {!hasRefundablePayment && (
              <div className="p-sm bg-warning/10 border border-warning/20 rounded-lg mb-md">
                <div className="flex items-start gap-sm">
                  <AlertCircle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-warning">
                    {t('bookingFinancial.bookkeepingOnlyNotice')}
                  </p>
                </div>
              </div>
            )}

            {/* Refund scope selector — shown only when the booking has both
                a booking payment and damage charges. Without this, LIFO would
                refund the most recent transaction (usually the damage), which
                may not be what the admin wants. */}
            {hasMixedTransactionTypes && (
              <div className="mb-md">
                <label className="block text-sm font-medium text-primary mb-xs">
                  {t('bookingFinancial.refundScope', 'Refund what?')}
                </label>
                <div className="flex flex-col sm:flex-row gap-sm">
                  <label className="flex items-start gap-sm cursor-pointer flex-1 p-sm border border-primary rounded-lg">
                    <input
                      type="radio"
                      checked={refundScope === 'booking_payment'}
                      onChange={() => { setRefundScope('booking_payment'); setRefundInput(''); }}
                      disabled={processingRefund}
                      className="h-4 w-4 mt-0.5 text-blue focus:ring-blue"
                    />
                    <span className="text-sm text-primary">
                      <span className="font-medium">{t('bookingFinancial.refundScopeBooking', 'Booking payment')}</span>
                      <span className="block text-xs text-secondary">
                        {formatPrice(bookingTransactions.reduce((s, tx) => s + tx.amount, 0))}
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-sm cursor-pointer flex-1 p-sm border border-primary rounded-lg">
                    <input
                      type="radio"
                      checked={refundScope === 'damage_charge'}
                      onChange={() => { setRefundScope('damage_charge'); setRefundInput(''); }}
                      disabled={processingRefund}
                      className="h-4 w-4 mt-0.5 text-blue focus:ring-blue"
                    />
                    <span className="text-sm text-primary">
                      <span className="font-medium flex items-center gap-xs">
                        <Wrench className="w-3 h-3 text-warning" />
                        {t('bookingFinancial.refundScopeDamage', 'Damage charge')}
                      </span>
                      <span className="block text-xs text-secondary">
                        {formatPrice(damagePaidTotal)}
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* External (already-refunded) toggle — forces bookkeeping-only mode
                on the backend so no Stripe/PayPal API call is made. This is
                the only way to refund PayPal-only bookings or the non-Stripe
                portion of mixed-payment bookings from this panel. */}
            {hasRefundablePayment && (
              <div className="mb-md">
                <label className="flex items-start gap-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isExternalRefund}
                    onChange={(e) => {
                      setIsExternalRefund(e.target.checked);
                      setRefundInput('');
                    }}
                    disabled={processingRefund}
                    className="h-4 w-4 mt-1 rounded border-primary text-blue focus:ring-blue"
                  />
                  <span className="text-sm text-primary">
                    <span className="font-medium">{t('bookingFinancial.externalRefundToggle', 'Already refunded externally')}</span>
                    <span className="block text-xs text-secondary mt-xs">
                      {t('bookingFinancial.externalRefundToggleHelp', 'Check this if the refund has already been issued outside this panel (PayPal dashboard, Stripe dashboard, bank transfer, cash). This records the refund in the ledger without moving any money.')}
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* Refund Type Toggle */}
            <div className="mb-md">
              <label className="block text-sm font-medium text-primary mb-xs">{t('bookingFinancial.refundType')}</label>
              <div className="flex rounded-lg overflow-hidden border border-primary w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => { setRefundMode('percentage'); setRefundInput(''); }}
                  className={`flex-1 sm:flex-none px-md sm:px-lg py-sm text-sm font-medium transition-colors ${
                    refundMode === 'percentage'
                      ? 'bg-blue text-white'
                      : 'bg-secondary text-secondary hover:text-primary'
                  }`}
                >
                  {t('bookingFinancial.percentage')}
                </button>
                <button
                  type="button"
                  onClick={() => { setRefundMode('fixed'); setRefundInput(''); }}
                  className={`flex-1 sm:flex-none px-md sm:px-lg py-sm text-sm font-medium transition-colors ${
                    refundMode === 'fixed'
                      ? 'bg-blue text-white'
                      : 'bg-secondary text-secondary hover:text-primary'
                  }`}
                >
                  {t('bookingFinancial.fixedAmount')}
                </button>
              </div>
            </div>

            {/* Amount Input */}
            <div className="mb-md">
              <label className="block text-sm font-medium text-primary mb-xs">
                {refundMode === 'percentage' ? t('bookingFinancial.refundPercentage') : t('bookingFinancial.refundAmount')}
              </label>
              <div className="flex flex-wrap items-center gap-sm sm:gap-md">
                <div className="relative w-full sm:w-auto" style={{ maxWidth: '200px' }}>
                  <input
                    type="number"
                    value={refundInput}
                    onChange={(e) => setRefundInput(e.target.value)}
                    min="0"
                    max={refundMode === 'percentage' ? '100' : effectiveRefundLimit}
                    step={refundMode === 'percentage' ? '1' : '0.01'}
                    placeholder={refundMode === 'percentage' ? t('bookingFinancial.percentagePlaceholder') : t('bookingFinancial.amountPlaceholder')}
                    className="input w-full"
                    style={refundMode === 'percentage' ? { paddingRight: '2.5rem' } : undefined}
                    disabled={processingRefund}
                  />
                  {refundMode === 'percentage' && (
                    <span className="absolute top-1/2 -translate-y-1/2 text-secondary text-sm pointer-events-none" style={{ right: '1.75rem' }}>%</span>
                  )}
                </div>
                {refundInput && calculatedRefundAmount > 0 && (
                  <span className="text-sm text-secondary">
                    = <strong className="text-primary">{formatPrice(calculatedRefundAmount)}</strong>
                    {refundMode === 'percentage' && (
                      <span> {t('common.of')} {formatPrice(booking.total_amount)}</span>
                    )}
                  </span>
                )}
              </div>
              {refundMode === 'percentage' && parseFloat(refundInput) > 100 && (
                <p className="text-xs text-danger mt-xs">{t('bookingFinancial.percentageCannotExceed')}</p>
              )}
              {refundMode === 'fixed' && parseFloat(refundInput) > effectiveRefundLimit && (
                <p className="text-xs text-danger mt-xs">
                  {t('bookingFinancial.amountExceedsRefundable', { amount: formatPrice(effectiveRefundLimit) })}
                </p>
              )}
            </div>

            {/* Reason (optional) */}
            <div className="mb-md">
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('bookingFinancial.reason')} <span className="text-secondary font-normal">({t('bookingFinancial.optional')})</span>
              </label>
              <input
                type="text"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                placeholder={t('bookingFinancial.reasonPlaceholder')}
                className="input w-full"
                disabled={processingRefund}
              />
            </div>

            {/* Restores balance — only meaningful on the booking ledger. Unticked
                by default (cancellation / goodwill), pre-ticked when the booking
                is overpaid. See refund_history.restores_balance. */}
            {refundScope !== 'damage_charge' && (
              <label className="flex items-start gap-sm mb-md cursor-pointer" data-testid="refund-restores-balance">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={refundRestoresBalance}
                  onChange={(e) => setRefundRestoresBalanceChoice(e.target.checked)}
                  disabled={processingRefund}
                />
                <span className="text-sm">
                  <span className="font-medium text-primary">{t('bookingFinancial.restoresBalanceLabel')}</span>
                  <span className="block text-xs text-secondary mt-xs">{t('bookingFinancial.restoresBalanceHelp')}</span>
                </span>
              </label>
            )}

            {/* Submit Button */}
            <button
              onClick={handleRefundSubmit}
              disabled={!isRefundValid() || processingRefund}
              className="btn btn-danger"
            >
              {processingRefund ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('bookingFinancial.processing')}
                </>
              ) : (
                <>
                  <RotateCcw className="w-4 h-4" />
                  {isExternalRefund
                    ? t('bookingFinancial.recordExternalRefund', 'Record external refund')
                    : t('bookingFinancial.issueRefund')}
                </>
              )}
            </button>
          </div>
        )}

        {/* Viewer cannot refund notice */}
        {!canManagePayments && (
          <div className="p-sm bg-tertiary rounded-lg mb-lg">
            <p className="text-sm text-secondary">{t('bookingFinancial.noPermission')}</p>
          </div>
        )}

        {/* Fully refunded notice */}
        {canManagePayments && remainingRefundable <= 0 && totalRefunded > 0 && (
          <div className="p-sm bg-success/10 border border-success/20 rounded-lg mb-lg">
            <p className="text-sm text-success font-medium">{t('bookingFinancial.fullyRefunded')}</p>
          </div>
        )}

        {/* Refund History */}
        <div>
          <h4 className="text-sm font-semibold text-primary mb-md flex items-center gap-xs">
            <Clock className="w-4 h-4 text-secondary" />
            {t('bookingFinancial.refundHistory')}
          </h4>

          {loadingRefunds ? (
            <div className="flex items-center gap-sm text-secondary text-sm py-md">
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('bookingFinancial.loadingRefundHistory')}
            </div>
          ) : refundHistory.length === 0 ? (
            <p className="text-sm text-secondary py-md">{t('bookingFinancial.noRefundsIssued')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-primary">
                    <th className="text-left py-sm pr-md text-secondary font-medium">{t('bookingFinancial.date')}</th>
                    <th className="text-left py-sm pr-md text-secondary font-medium">{t('bookingFinancial.type')}</th>
                    <th className="text-right py-sm pr-md text-secondary font-medium">{t('bookingFinancial.amount')}</th>
                    <th className="text-left py-sm pr-md text-secondary font-medium">{t('bookingFinancial.provider', 'Provider')}</th>
                    <th className="text-left py-sm pr-md text-secondary font-medium">{t('bookingFinancial.refundId', 'Refund ID')}</th>
                    <th className="text-left py-sm text-secondary font-medium">{t('bookingFinancial.reason')}</th>
                  </tr>
                </thead>
                <tbody>
                  {refundHistory.map((refund) => (
                    <tr key={refund.id} className="border-b border-primary/50">
                      <td className="py-sm pr-md text-primary whitespace-nowrap">
                        {formatDateTimeCompact(refund.created_at)}
                      </td>
                      <td className="py-sm pr-md">
                        <div className="flex items-center gap-xs">
                          <span className="text-primary">
                            {refund.refund_type === 'percentage'
                              ? `${refund.refund_value}%`
                              : t('bookingFinancial.fixed')}
                          </span>
                          {refund.is_bookkeeping_only && (
                            <span className="inline-flex items-center gap-xs px-xs py-0.5 text-xs font-medium bg-warning/10 text-warning rounded">
                              <BookOpen className="w-3 h-3" />
                              {t('bookingFinancial.bookkeeping')}
                            </span>
                          )}
                          {refund.restores_balance && (
                            <span
                              className="inline-flex items-center px-xs py-0.5 text-xs font-medium bg-info/10 text-info rounded"
                              title={t('bookingFinancial.restoresBalanceHelp')}
                            >
                              {t('bookingFinancial.restoresBalanceBadge')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-sm pr-md text-right font-medium text-danger">
                        {formatPrice(refund.refund_amount)}
                      </td>
                      <td className="py-sm pr-md">
                        <span className={`inline-flex items-center px-xs py-0.5 text-xs font-medium rounded ${
                          (refund as any).paypal_refund_id
                            ? 'bg-blue/10 text-blue'
                            : refund.stripe_refund_id
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-gray-100 text-gray-600'
                        }`}>
                          {(refund as any).paypal_refund_id ? 'PayPal' : refund.stripe_refund_id ? 'Stripe' : t('bookingFinancial.bookkeeping', 'Bookkeeping')}
                        </span>
                      </td>
                      <td className="py-sm pr-md font-mono text-xs text-secondary break-all">
                        {refund.stripe_refund_id || (refund as any).paypal_refund_id || '\u2014'}
                      </td>
                      <td className="py-sm text-secondary">
                        {refund.reason || '\u2014'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            className="bg-primary rounded-xl shadow-2xl p-xl w-full max-w-md mx-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-primary mb-md flex items-center gap-xs">
              <AlertCircle className="w-5 h-5 text-danger" />
              {isExternalRefund
                ? t('bookingFinancial.confirmExternalRefund', 'Confirm external refund record')
                : t('bookingFinancial.confirmRefund')}
            </h3>

            <div className="space-y-md mb-lg">
              <div className="bg-tertiary rounded-lg p-md space-y-sm">
                <div className="flex justify-between text-sm">
                  <span className="text-secondary">{t('bookingFinancial.booking')}:</span>
                  <span className="font-medium text-primary">#{booking.order_id}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-secondary">{t('bookingFinancial.refundAmount')}:</span>
                  <span className="font-bold text-danger text-lg">{formatPrice(calculatedRefundAmount)}</span>
                </div>
                {refundMode === 'percentage' && (
                  <div className="flex justify-between text-sm">
                    <span className="text-secondary">{t('bookingFinancial.percentage')}:</span>
                    <span className="text-primary">{refundInput}% {t('common.of')} {formatPrice(booking.total_amount)}</span>
                  </div>
                )}
                {refundReason && (
                  <div className="flex justify-between text-sm">
                    <span className="text-secondary">{t('bookingFinancial.reason')}:</span>
                    <span className="text-primary">{refundReason}</span>
                  </div>
                )}
                {refundScope !== 'damage_charge' && refundRestoresBalance && (
                  <div className="flex items-center gap-xs mt-sm">
                    <span className="inline-flex items-center gap-xs px-sm py-xs text-xs font-medium bg-info/10 text-info border border-info/20 rounded">
                      {t('bookingFinancial.restoresBalanceBadge')}
                    </span>
                  </div>
                )}
                {(isExternalRefund || !hasRefundablePayment) && (
                  <div className="flex items-center gap-xs mt-sm">
                    <span className="inline-flex items-center gap-xs px-sm py-xs text-xs font-medium bg-warning/10 text-warning border border-warning/20 rounded">
                      <BookOpen className="w-3 h-3" />
                      {t('bookingFinancial.bookkeepingOnlyNoTransfer')}
                    </span>
                  </div>
                )}
              </div>

              <p className="text-sm text-secondary">
                {isExternalRefund
                  ? t('bookingFinancial.externalRefundWarning', 'This will record the refund in the ledger only — no money will be transferred. Make sure the refund has actually been issued externally before confirming.')
                  : hasRefundablePayment
                    ? (hasPayPalPayment
                      ? t('bookingFinancial.paypalRefundWarning')
                      : t('bookingFinancial.stripeRefundWarning'))
                    : t('bookingFinancial.bookkeepingRefundWarning')}
              </p>
            </div>

            <div className="flex justify-end gap-md">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="btn btn-secondary"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmRefund}
                className="btn btn-danger"
              >
                <RotateCcw className="w-4 h-4" />
                {t('bookingFinancial.confirmRefund')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
